import { useRef, useEffect } from "react";
import { ChevronRight } from "lucide-react";
import type { ExploreResultItem } from "@/types/exploreResult";
import { itemKey } from "@/hooks/useExploreResults";

interface ExploreRailProps {
  results: ExploreResultItem[];
  selectedKey: string | null;
  onSelectKey: (key: string) => void;
  onOpenDetails: (key: string) => void;
}

const ExploreRail = ({ results, selectedKey, onSelectKey, onOpenDetails }: ExploreRailProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to selected pill
  useEffect(() => {
    if (!selectedKey || !scrollRef.current) return;
    const el = scrollRef.current.querySelector(`[data-key="${selectedKey}"]`) as HTMLElement | null;
    el?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [selectedKey]);

  if (results.length === 0) return null;

  return (
    <div
      ref={scrollRef}
      data-testid="explore-rail"
      className="flex gap-2 overflow-x-auto scrollbar-none snap-x snap-mandatory pb-1 px-1"
      style={{ WebkitOverflowScrolling: "touch" }}
    >
      {results.map((item) => {
        const key = itemKey(item);
        const isSelected = key === selectedKey;
        return (
          <button
            key={key}
            data-key={key}
            data-testid={isSelected ? "explore-rail-card-selected" : "explore-rail-card"}
            onClick={() => isSelected ? onOpenDetails(key) : onSelectKey(key)}
            className={`
              snap-center shrink-0 flex items-center gap-2 h-11 pl-1 pr-2.5
              rounded-full bg-card/95 backdrop-blur-sm text-card-foreground
              shadow-sm border transition-all duration-150
              active:scale-[0.97]
              ${isSelected
                ? "border-primary/40 ring-1 ring-primary/20 shadow-md"
                : "border-border/30"
              }
            `}
          >
            {/* Thumbnail */}
            <div className="h-9 w-9 rounded-full bg-muted overflow-hidden shrink-0">
              {item.image ? (
                <img src={item.image} alt="" className="h-full w-full object-cover" loading="lazy" />
              ) : (
                <div className="h-full w-full flex items-center justify-center text-muted-foreground/30 text-[8px]">
                  —
                </div>
              )}
            </div>
            {/* Title */}
            <span className="text-[13px] font-medium leading-tight truncate max-w-[140px]">
              {item.title}
            </span>
            {/* Expand hint on selected */}
            {isSelected && (
              <ChevronRight className="h-3.5 w-3.5 text-primary shrink-0 -mr-0.5" />
            )}
          </button>
        );
      })}
    </div>
  );
};

export default ExploreRail;
